<script lang="ts">
  import { marked } from 'marked';
  import { processMarkdownImages } from '@lib/utils/markdown-images';

  export let content: string = '';
  export let align: 'left' | 'center' | 'right' = 'left';
  export let inline = false;
  export let color: string | undefined = undefined;

  function parseMarkdown(text: string): string {
    if (!text) return '';
    try {
      // Process custom image tags before markdown parsing
      const withImages = processMarkdownImages(text);
      return marked(withImages) as string;
    } catch (e) {
      console.error('Error parsing markdown:', e);
      return text;
    }
  }

  $: processedContent = parseMarkdown(content);
</script>

<div class="markdown-container" class:inline style:text-align={align} style:color={color}>
  <div class="markdown-content">
    {@html processedContent}
  </div>
</div>

<style lang="postcss">
  .markdown-container {
    container-type: inline-size;
    container-name: markdown;
    width: 100%;
  }

  .markdown-container.inline {
    container-type: normal; /* Disable container queries for inline markdown */
  }

  .markdown-content {
    width: 100%;
    color: inherit;
  }
  
  /* Ensure all markdown-generated elements inherit the color */
  .markdown-content :global(p),
  .markdown-content :global(h1),
  .markdown-content :global(h2),
  .markdown-content :global(h3),
  .markdown-content :global(h4),
  .markdown-content :global(h5),
  .markdown-content :global(h6),
  .markdown-content :global(li),
  .markdown-content :global(span) {
    color: inherit;
  }

  /* Base typography for markdown */
  .markdown-content :global(h1) {
    font-size: 2rem;
    margin: 1rem 0;
    font-weight: 600;
    line-height: 1.2;
    /* color: var(--text-color); */
  }

  .markdown-content :global(h2) {
    font-size: 1.5rem;
    margin: 1rem 0;
    font-weight: 600;
    line-height: 1.2;
    /* color: var(--text-color); */
  }

  .markdown-content :global(h3) {
    font-size: 1.25rem;
    margin: 1rem 0;
    font-weight: 600;
    line-height: 1.2;
    /* color: var(--text-color); */
  }

  .markdown-content :global(h4),
  .markdown-content :global(h5),
  .markdown-content :global(h6) {
    font-size: 1.125rem;
    margin: 1rem 0;
    font-weight: 600;
    line-height: 1.2;
    /* color: var(--text-color); */
  }

  .markdown-content :global(p) {
    font-size: 1.1rem;
    margin: 0.5rem 0;
    line-height: 1.4;
    /* color: var(--text-color); */
  }

  .markdown-content :global(ul),
  .markdown-content :global(ol) {
    margin: 0.5rem 0;
    padding-left: 1.5rem;
    list-style-position: outside;
    text-align: left;
  }

  .markdown-content :global(li) {
    margin: 0.25rem 0;
    font-size: 1.05rem;
    line-height: 1.4;
  }

  .markdown-content :global(strong) {
    font-weight: 600;
  }

  .markdown-content :global(em) {
    font-style: italic;
  }

  .markdown-content :global(code) {
    font-family: monospace;
    font-size: 0.9em;
    padding: 0.125rem 0.25rem;
    background-color: rgba(0, 0, 0, 0.05);
    border-radius: 3px;
  }

  .markdown-content :global(pre) {
    margin: 1rem 0;
    padding: 1rem;
    background-color: rgba(0, 0, 0, 0.05);
    border-radius: 5px;
    overflow-x: auto;
  }

  .markdown-content :global(pre code) {
    background-color: transparent;
    padding: 0;
    font-size: 0.875rem;
  }

  .markdown-content :global(blockquote) {
    margin: 1rem 0;
    padding-left: 1rem;
    border-left: 3px solid var(--border-color, #e5e5e5);
    font-style: italic;
    opacity: 0.85;
  }

  .markdown-content :global(table) {
    width: 100%;
    border-collapse: collapse;
    margin: 1rem 0;
  }

  .markdown-content :global(th),
  .markdown-content :global(td) {
    padding: 0.5rem;
    text-align: left;
    border-bottom: 1px solid var(--border-color, #e5e5e5);
  }

  .markdown-content :global(th) {
    font-weight: 600;
  }

  .markdown-content :global(hr) {
    margin: 1.5rem 0;
    border: none;
    border-top: 1px solid var(--border-color, #e5e5e5);
  }

  .markdown-content :global(a) {
    color: var(--theme-primary-color, #0066cc);
    text-decoration: underline;
  }

  .markdown-content :global(a:hover) {
    opacity: 0.8;
  }

  /* Markdown embedded images */
  .markdown-content :global(img.markdown-image) {
    max-width: 100%;
    height: auto;
    border-radius: var(--theme-radius, 0.5rem);
    margin: 1.5rem 0;
    display: block;
  }

  .markdown-content :global(figure.markdown-image) {
    margin: 2rem 0;
    text-align: center;
  }

  .markdown-content :global(figure.markdown-image img) {
    max-width: 100%;
    height: auto;
    border-radius: var(--theme-radius, 0.5rem);
    margin: 0 auto;
    display: block;
  }

  .markdown-content :global(figure.markdown-image figcaption) {
    margin-top: 0.75rem;
    font-size: 0.875rem;
    color: var(--theme-text-color, inherit);
    opacity: 0.7;
    font-style: italic;
  }

  /* Remove default margins from first/last elements */
  .markdown-content > :global(*:first-child) {
    margin-top: 0;
  }

  .markdown-content > :global(*:last-child) {
    margin-bottom: 0;
  }

  /* Dark mode adjustments */
  :global([data-theme='dark']) .markdown-content :global(code) {
    background-color: rgba(255, 255, 255, 0.1);
  }

  :global([data-theme='dark']) .markdown-content :global(pre) {
    background-color: rgba(255, 255, 255, 0.1);
  }

  /* Container queries for responsive typography */
  @container markdown (max-width: 350px) {
    .markdown-content :global(h1) {
      font-size: 1.5rem;
    }

    .markdown-content :global(h2) {
      font-size: 1.25rem;
    }

    .markdown-content :global(h3) {
      font-size: 1.125rem;
    }

    .markdown-content :global(h4),
    .markdown-content :global(h5),
    .markdown-content :global(h6) {
      font-size: 1rem;
    }

    .markdown-content :global(p) {
      font-size: 1rem;
    }

    .markdown-content :global(li) {
      font-size: 0.95rem;
    }
  }

  @container markdown (max-width: 280px) {
    .markdown-content :global(h1) {
      font-size: 1.125rem;
      margin: 0.75rem 0;
    }

    .markdown-content :global(h2) {
      font-size: 1rem;
      margin: 0.75rem 0;
    }

    .markdown-content :global(h3) {
      font-size: 0.925rem;
      margin: 0.75rem 0;
    }

    .markdown-content :global(h4),
    .markdown-content :global(h5),
    .markdown-content :global(h6) {
      font-size: 0.875rem;
      margin: 0.75rem 0;
    }

    .markdown-content :global(p) {
      font-size: 0.875rem;
      margin: 0.375rem 0;
      line-height: 1.3;
    }

    .markdown-content :global(ul),
    .markdown-content :global(ol) {
      padding-left: 1.25rem;
      margin: 0.375rem 0;
    }

    .markdown-content :global(li) {
      font-size: 0.8125rem;
      margin: 0.125rem 0;
    }

    .markdown-content :global(pre) {
      padding: 0.75rem;
      font-size: 0.75rem;
    }

    .markdown-content :global(blockquote) {
      font-size: 0.875rem;
      padding-left: 0.75rem;
    }

    .markdown-content :global(th),
    .markdown-content :global(td) {
      padding: 0.375rem;
      font-size: 0.8125rem;
    }

    .markdown-content :global(code) {
      font-size: 0.85em;
    }
    .markdown-content :global(pre code) {
      font-size: 0.75em;
    }
  }
</style>